import React from 'react';
import { ArrowRightLeft, ArrowRight } from 'lucide-react';
import { Transaction } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { txFeeInDisplay, txNetworkFeeInDisplay, txTotalInDisplay } from '../../utils/displayAmount';

// Human relative time, coarse buckets — "Today", "5 days ago", "3 weeks ago".
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const now = Date.now();
  const days = Math.floor((now - then) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} week${w === 1 ? '' : 's'} ago`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} month${m === 1 ? '' : 's'} ago`;
  }
  const y = Math.floor(days / 365);
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

// Colored BUY / SELL / TRANSFER pill.
export function TypePill({ type }: { type: Transaction['type'] }) {
  const map = {
    BUY: { bg: 'rgba(109,185,129,0.12)', color: 'var(--color-positive)', label: 'BUY' },
    SELL: { bg: 'rgba(196,97,94,0.12)', color: 'var(--color-negative)', label: 'SELL' },
    TRANSFER: { bg: 'rgba(184,153,104,0.14)', color: '#b89968', label: 'TRANSFER' },
  } as const;
  const s = map[type];
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md tracking-wide"
      style={{ background: s.bg, color: s.color, border: `0.5px solid ${s.color}33` }}
    >
      {type === 'TRANSFER' && <ArrowRightLeft className="w-2.5 h-2.5" />}
      {s.label}
    </span>
  );
}

// Wallet chip — single wallet for BUY/SELL, from → to for TRANSFER.
export function WalletCell({ tx }: { tx: Transaction }) {
  const chipStyle: React.CSSProperties = {
    background: 'rgb(var(--overlay) / 0.04)',
    border: '0.5px solid rgb(var(--overlay) / 0.08)',
    color: 'var(--color-sub)',
  };
  if (tx.type === 'TRANSFER') {
    return (
      <div className="inline-flex items-center gap-1 flex-wrap">
        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-md font-medium whitespace-nowrap" style={chipStyle}>
          {tx.fromWallet ?? '—'}
        </span>
        <ArrowRight className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--color-sub)' }} />
        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-md font-medium whitespace-nowrap" style={chipStyle}>
          {tx.toWallet ?? '—'}
        </span>
      </div>
    );
  }
  if (tx.wallet) {
    return (
      <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-md font-medium whitespace-nowrap" style={chipStyle}>
        {tx.wallet}
      </span>
    );
  }
  return <span className="text-xs" style={{ color: 'var(--color-sub)' }}>—</span>;
}

/**
 * A transaction's total, with its fee accounted for — the one place that
 * rendering lives, so the all-transactions table, the per-coin view and the coin
 * page cannot drift apart.
 *
 * BUY totals are shown INCLUDING the fee and SELL totals NET of it, because that
 * is the cash that actually moved, and it is the same direction the fee moves the
 * taxable gain. The fee is then named underneath rather than silently folded in,
 * so the headline number is always reconcilable.
 *
 * A TRANSFER has no total — only its network fee, which is what gets shown.
 */
export function TotalCell({ tx }: { tx: Transaction }) {
  const { fmtDisplay, currency, rate } = useCurrency();
  const code = currency.code;

  if (tx.type === 'TRANSFER') {
    const netFee = txNetworkFeeInDisplay(tx, code, rate);
    return tx.networkFee && tx.networkFee > 0 ? (
      <span style={{ color: 'var(--color-sub)' }}>
        {fmtDisplay(netFee.value)} <span className="text-[9px]">fee</span>
      </span>
    ) : (
      <>—</>
    );
  }

  const total = txTotalInDisplay(tx, code, rate);
  const fee = txFeeInDisplay(tx, code, rate);

  if (fee.value <= 0) return <>{fmtDisplay(total.value)}</>;

  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span>{fmtDisplay(total.value)}</span>
      <span className="text-[9px] font-normal" style={{ color: 'var(--color-sub)' }}>
        {tx.type === 'BUY' ? 'incl.' : 'net of'} {fmtDisplay(fee.value)} fee
      </span>
    </span>
  );
}
